import React, {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { Category } from 'src/model/Category';
import type { Grade } from 'src/model/Grade';
import type { GradeSize } from 'src/model/GradeSize';
import type { Product } from 'src/model/Product';

import { X, Package, Tag, DollarSign, Truck, ImageIcon } from 'lucide-react';
import BasicInfo from '../components/BasicInfo';
import Button from '../components/button';
import CategoriesTags from '../components/CategoriesTags';
import ValuesVariations from '../components/ValuesVariations';
import { WarrantyShippingDimensions } from '../components/WarrantyShippingDimensions';
import { UploadImagens } from '../components/UploadImagens';

interface EditProductUIProps {
  formData: Product;
  formErrors: Record<string, string[]>;
  categoryOptions: Category[];
  selectedCategories: Category[];
  inputTags: string[];
  filesImage: File[];
  previewFiles: string[];
  FileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (args: {
    index: number;
    productId?: string | number;
    imageUrl?: string;
  }) => void;
  isGrade: boolean;
  grade: Grade[];
  setGrade: Dispatch<SetStateAction<Grade[]>>;
  filesImageGrade: File[];
  previewFilesGrade: string;
  previewGradesSizes: GradeSize[];
  shippingCost: string | null;
  addQuantiValue: (e: React.FormEvent) => void;
  removeGrade: (index: string) => void;
  addSize: (e: React.FormEvent) => void;
  onChangeFormInput: (inputName: string) => (value: string) => void;
  handleInputChange: (field: string) => (value: string) => void;
  onSelectCategory: (e: React.FocusEvent<HTMLSelectElement>) => void;
  onRemoveCategory: (value: string) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (value: string) => void;
  onGrade: (inputName: string) => (value: string) => void;
  onChangeFormFileInputGradeSize: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onShippingCalc: (
    height: string,
    width: string,
    depth: string,
    weight: string
  ) => void;
  onSaveSection: (section: string, data: Partial<Product>) => void;
  onSaveCategory: (section: string, data: Partial<Product>) => void;
  handleSaveImages: () => void;
  onSaveWarrantyDimensions: () => void;
  onClose?: () => void;
}

type Section =
  | 'basicInfo'
  | 'categoriesTags'
  | 'valuesVariations'
  | 'warrantyShippingDimensions'
  | 'uploadImages';

const EditProductUI: React.FC<EditProductUIProps> = ({
  formData,
  formErrors,
  categoryOptions,
  selectedCategories,
  inputTags = [],
  filesImage,
  previewFiles,
  FileUpload,
  onDeleteFile,
  isGrade,
  grade,
  setGrade,
  addQuantiValue,
  removeGrade,
  addSize,
  onChangeFormInput,
  handleInputChange,
  onSelectCategory,
  onRemoveCategory,
  onAddTag,
  onRemoveTag,
  onGrade,
  onChangeFormFileInputGradeSize,
  filesImageGrade,
  previewFilesGrade,
  previewGradesSizes,
  onShippingCalc,
  shippingCost,
  onSaveSection,
  onSaveCategory,
  handleSaveImages,
  onSaveWarrantyDimensions,
  onClose,
}): React.ReactElement => {
  const [activeSection, setActiveSection] = useState<Section>('basicInfo');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const navigationItems = [
    { id: 'basicInfo' as Section, label: 'Informações Básicas', icon: Package },
    { id: 'categoriesTags' as Section, label: 'Categorias e Tags', icon: Tag },
    {
      id: 'valuesVariations' as Section,
      label: 'Valores e Variações',
      icon: DollarSign,
    },
    {
      id: 'warrantyShippingDimensions' as Section,
      label: 'Garantia e Frete',
      icon: Truck,
    },
    { id: 'uploadImages' as Section, label: 'Imagens', icon: ImageIcon },
  ];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleExit = () => onClose?.();

  const requestConfirmation = (action: () => void) => {
    setPendingAction(() => action);
    setConfirmOpen(true);
  };

  const confirmAndRun = () => {
    const fn = pendingAction;
    setConfirmOpen(false);
    setPendingAction(null);
    fn?.();
  };

  const cancelConfirmation = () => {
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'basicInfo':
        return (
          <div className="space-y-6">
            <BasicInfo
              formData={formData}
              formErrors={formErrors}
              onChangeFormInput={onChangeFormInput}
            />
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={() =>
                  requestConfirmation(() =>
                    onSaveSection('basicInfo', {
                      name: formData.name,
                      description: formData.description,
                      sku: formData.sku,
                      brand: formData.brand,
                    })
                  )
                }
                buttonStyle="red"
                title="Salvar Alterações"
              />
            </div>
          </div>
        );
      case 'categoriesTags':
        return (
          <div className="space-y-6">
            <CategoriesTags
              categoryOptions={categoryOptions}
              selectedCategories={selectedCategories}
              inputTags={inputTags ?? []}
              onSelectCategory={onSelectCategory}
              onRemoveCategory={onRemoveCategory}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
            />
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={() =>
                  requestConfirmation(() =>
                    onSaveCategory('categoriesTags', {
                      category: selectedCategories,
                      tags: inputTags,
                    })
                  )
                }
                buttonStyle="red"
                title="Salvar Alterações"
              />
            </div>
          </div>
        );
      case 'valuesVariations':
        return (
          <div className="space-y-6">
            <ValuesVariations
              product={formData}
              formErrors={formErrors}
              isGrade={isGrade}
              setGrade={setGrade}
              grade={grade}
              onChangeFormInput={onChangeFormInput}
              addQuantiValue={addQuantiValue}
              removeGrade={removeGrade}
              addSize={addSize}
              onGrade={onGrade}
              onChangeFormFileInputGradeSize={onChangeFormFileInputGradeSize}
              filesImageGrade={filesImageGrade}
              previewFilesGrade={previewFilesGrade}
              previewGradesSizes={previewGradesSizes}
            />
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={() =>
                  requestConfirmation(() =>
                    onSaveSection('valuesVariations', {
                      price: formData.price,
                      stock: formData.stock,
                    })
                  )
                }
                buttonStyle="red"
                title="Salvar Alterações"
              />
            </div>
          </div>
        );
      case 'warrantyShippingDimensions':
        return (
          <div className="space-y-6">
            <WarrantyShippingDimensions
              formData={formData}
              formErrors={formErrors}
              onChangeFormInput={onChangeFormInput}
              onShippingCalc={onShippingCalc}
              shippingCost={shippingCost}
            />
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={() => requestConfirmation(onSaveWarrantyDimensions)}
                buttonStyle="red"
                title="Salvar Alterações"
              />
            </div>
          </div>
        );
      case 'uploadImages':
        return (
          <div className="space-y-6">
            <UploadImagens
              productId={formData.id}
              filesImage={filesImage}
              previewFiles={previewFiles}
              FileUpload={FileUpload}
              onDeleteFile={onDeleteFile}
            />
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={() => requestConfirmation(handleSaveImages)}
                buttonStyle="red"
                title="ações"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[600px] bg-background">
      <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-red-600" />
          <div>
            <h1 className="text-lg font-semibold text-foreground">
              Editar Produto
            </h1>
            <p className="text-sm text-muted-foreground">{formData.name}</p>
          </div>
        </div>
        {onClose && (
          <Button
            onClick={handleExit}
            className="border-2 border-red-600 text-white bg-red-600 hover:bg-red-600"
            title="Sair da Edição"
          />
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 border-r border-border bg-card overflow-y-auto flex-shrink-0">
          <nav className="p-4 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    isActive
                      ? 'bg-red-600 text-red-600-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto bg-muted/30">
          <div className="max-w-5xl mx-auto p-8">{renderActiveSection()}</div>
        </main>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={cancelConfirmation}
          />
          <div className="relative bg-white border border-border rounded-2xl shadow-lg w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-base font-semibold text-foreground">
                Confirmar envio
              </h2>
              <button
                onClick={cancelConfirmation}
                className="p-1 rounded-md hover:bg-muted"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Só será possível alterar novamente este produto após a aprovação
              da modificação atual.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button title="Cancelar" onClick={cancelConfirmation} />
              <Button
                title="Confirmar"
                buttonStyle="red"
                onClick={confirmAndRun}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProductUI;
